import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { Mark } from "./Mark";
import { EASE } from "./Motion";
import { scrollToTarget, setScrollLock } from "@/lib/scroll";

const KEY = "jh_welcome_seen";

const seen = () => {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
};

export const WelcomeNote = () => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (seen()) return undefined;
    const t = setTimeout(() => setOpen(true), 3200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    setScrollLock(open);
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const close = (target) => {
    try {
      localStorage.setItem(KEY, "1");
    } catch {
      /* storage unavailable */
    }
    setOpen(false);
    if (target) setTimeout(() => scrollToTarget(target), 450);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="welcome-overlay"
          className="fixed inset-0 z-[70] flex items-center justify-center bg-[#0a0b0d]/70 p-4 backdrop-blur-md sm:p-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          onClick={() => close()}
        >
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="welcome-title"
            data-testid="welcome-note"
            onClick={(e) => e.stopPropagation()}
            className="relative grid w-full max-w-4xl grid-cols-1 overflow-hidden border border-white/10 bg-[#121418] md:grid-cols-2"
            initial={{ clipPath: "inset(50% 0% 50% 0%)", y: 30 }}
            animate={{ clipPath: "inset(0% 0% 0% 0%)", y: 0 }}
            exit={{ clipPath: "inset(50% 0% 50% 0%)", y: 20 }}
            transition={{ duration: 0.9, ease: EASE }}
          >
            <button data-testid="welcome-close-btn" aria-label="Close welcome note" onClick={() => close()} className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full border border-white/25 bg-[#0a0b0d]/60 text-[#f5f3ef] backdrop-blur-md transition-colors duration-300 hover:border-[#c8a97e] hover:bg-[#c8a97e] hover:text-[#0a0b0d]">
              <X className="h-4 w-4" />
            </button>
            <div className="relative h-44 overflow-hidden md:h-auto">
              <motion.img src="/assets/SSF5.jpg" alt="Jaisun House showrooms" className="h-full w-full object-cover" initial={{ scale: 1.25 }} animate={{ scale: 1 }} transition={{ duration: 1.6, ease: EASE }} />
              <div className="absolute inset-0 bg-gradient-to-t from-[#121418] via-transparent to-transparent md:bg-gradient-to-r md:from-transparent md:to-[#121418]/40" />
            </div>
            <div className="p-7 sm:p-10">
              <Mark className="h-10 w-auto" />
              <p className="mt-6 font-mono text-xs uppercase tracking-[0.25em] text-[#c8a97e]">A note from the house</p>
              <h2 id="welcome-title" className="mt-4 font-display text-4xl leading-[0.95] sm:text-5xl">
                Welcome to <em className="text-[#c8a97e]">Jaisun House.</em>
              </h2>
              <p className="mt-5 text-sm leading-relaxed text-[#c9c6c0] sm:text-base">
                Home to internationally recognised fashion brands in Ireland since 1981. Explore our nine brands, or come and see the new season in person at our Dundrum showrooms.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button data-testid="welcome-explore-btn" onClick={() => close("#brands")} className="group flex items-center gap-2 rounded-full bg-[#c8a97e] px-6 py-3 text-[12px] font-medium uppercase tracking-[0.16em] text-[#0a0b0d] transition-colors duration-300 hover:bg-[#f5f3ef]">
                  Explore brands <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:rotate-45" />
                </button>
                <button data-testid="welcome-book-btn" onClick={() => close("#appointment")} className="link-u pb-1 text-[12px] uppercase tracking-[0.16em] text-[#f5f3ef]">
                  Book a visit
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
